import React from 'react';
import classNames from 'classnames';
import styles from './styles.module.scss';
import icCloseIcon from 'assets/svg/ic_close.svg';
import bgBtnConfirmHero from 'assets/svg/bg_btn_confirm_hero.svg';
import ErrorView from '@profile/src/Components/ErrorView';
import { ErrorViewType } from '@profile/src/Components/ErrorView/config';
import { cosmeticMap, heroMap, itemTypeMap, currencyMap } from './config';
import ImageWithPlaceholder from '@profile/src/Components/ImageWithPlaceHolder';
import { webImageUri } from '@profile/src/utils/uri';
import Spinner from '@profile/src/Components/Spinner';
import { openThetanBoxV2 } from '@profile/src/redux/api';
import { useTypedSelector } from '@profile/src/redux/utils';
import Modal from '@profile/src/Components/Modal';
import { getSkinInfo } from './utils';
import { BoxConfigRefType } from '@profile/src/utils/types';
import { formatCurrency } from '@wolffun/system-currency';

const OpenGiftBoxFallbackModal = ({
  onClose,
  onRefresh,
  boxConfig,
  amount
}) => {
  const cosmeticConfigs = useTypedSelector(state => state.cosmeticItemsConfigs.cosmeticConfigs);
  const [currentIndex, setCurrentIndex] = React.useState(1);
  const [currentOpenBox, setCurrentOpenBox] = React.useState();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const skinInfo = React.useMemo(() => {
    if (currentOpenBox) {
      return getSkinInfo(currentOpenBox, {
        cosmeticConfigs
      });
    }

    return undefined;
  }, [currentOpenBox, cosmeticConfigs]);
  const itemType = boxConfig ? itemTypeMap[boxConfig.refType] : undefined;

  const renderError = () => /*#__PURE__*/React.createElement(ErrorView, {
    className: styles.error,
    config: {
      type: ErrorViewType.Custom,
      title: 'OOPS!',
      content: 'Sorry, you can’t open box at that time. Please try again later'
    }
  });

  const openBox = React.useCallback(async () => {
    if (loading || currentOpenBox) {
      return;
    }

    setLoading(true);
    const res = await openThetanBoxV2({
      boxType: boxConfig ? boxConfig.boxType : -1
    });
    setLoading(false);

    if (res.status === 'error' || res.body === undefined) {
      setError(true);
      return;
    }

    setCurrentOpenBox(res.body);
  }, [boxConfig, currentOpenBox, loading]);

  const onClickConfirm = () => {
    if (currentIndex === amount) {
      onClose();
      onRefresh();
      return;
    }

    setCurrentIndex(prev => prev + 1);
    setCurrentOpenBox(undefined);
  };

  React.useEffect(() => {
    document.addEventListener('keydown', openBox);
    return () => {
      document.removeEventListener('keydown', openBox);
    };
  }, [openBox]);

  const renderResult = () => {
    if (!currentOpenBox) {
      return undefined;
    }

    switch (currentOpenBox.itemType) {
      case BoxConfigRefType.COSMETIC:
        return renderCosmetic();

      case BoxConfigRefType.HERO_SKIN:
        return renderHero();

      case BoxConfigRefType.CURRENCY:
        return renderCurrency();

      default:
        return undefined;
    }
  };

  const renderHero = () => {
    const hero = currentOpenBox ? currentOpenBox.data[BoxConfigRefType.HERO_SKIN] : undefined;
    const heroMapInfo = skinInfo && hero ? heroMap[hero.skinInfo.heroRarity] : undefined;

    if (!skinInfo || !heroMapInfo) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.content, styles.heroContent)
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.heroWrapper
    }, /*#__PURE__*/React.createElement("img", {
      className: styles.bgHero,
      src: heroMapInfo.image
    }), /*#__PURE__*/React.createElement(ImageWithPlaceholder, {
      className: styles.portrait,
      src: webImageUri(skinInfo.image),
      renderPlaceholder: () => /*#__PURE__*/React.createElement(Spinner, {
        secondary: true,
        className: styles.loading
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: styles.right
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.label, heroMapInfo.style)
    }, /*#__PURE__*/React.createElement("span", null, "you have unlocked")), /*#__PURE__*/React.createElement("div", {
      className: styles.wrapperName
    }, /*#__PURE__*/React.createElement("span", {
      className: classNames(styles.role, heroMapInfo.style)
    }, skinInfo.skinName), /*#__PURE__*/React.createElement("span", {
      className: styles.name
    }, skinInfo.name)), /*#__PURE__*/React.createElement("div", {
      className: styles.wrapperButton
    }, /*#__PURE__*/React.createElement("button", {
      onClick: onClickConfirm,
      className: styles.btnConfirm
    }, /*#__PURE__*/React.createElement("img", {
      draggable: false,
      className: styles.bg,
      src: bgBtnConfirmHero
    }), /*#__PURE__*/React.createElement("span", null, "Confirm"))))));
  };

  const renderCosmetic = () => {
    const cosmetic = currentOpenBox ? currentOpenBox.data[BoxConfigRefType.COSMETIC] : undefined;
    const cosmeticMapInfo = skinInfo && cosmetic ? cosmeticMap[cosmetic.rarity] : undefined;

    if (!skinInfo || !cosmeticMapInfo) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.content, styles.cosmeticContent)
    }, /*#__PURE__*/React.createElement("div", {
      onClick: () => onClickConfirm(),
      className: classNames(styles.cosmeticWrapper, cosmeticMapInfo.style)
    }, /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.glow, cosmeticMapInfo.glowStyle)
    }), /*#__PURE__*/React.createElement(ImageWithPlaceholder, {
      className: styles.portrait,
      src: webImageUri(skinInfo.image),
      renderPlaceholder: () => /*#__PURE__*/React.createElement(Spinner, {
        secondary: true,
        className: styles.loading
      })
    })));
  };

  const renderCurrency = () => {
    const currency = currentOpenBox ? currentOpenBox.data[BoxConfigRefType.CURRENCY] : undefined;
    const currencyMapInfo = currency ? currencyMap[currency.value.token] : undefined;

    if (!currencyMapInfo) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.content, styles.currencyContent)
    }, /*#__PURE__*/React.createElement("div", {
      onClick: () => onClickConfirm(),
      className: classNames(styles.currencyWrapper)
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.textContainer
    }, /*#__PURE__*/React.createElement("span", {
      className: classNames(styles.currencyName, currencyMapInfo.style)
    }, currencyMapInfo.name)), /*#__PURE__*/React.createElement(ImageWithPlaceholder, {
      className: styles.portrait,
      src: currencyMapInfo.image,
      renderPlaceholder: () => /*#__PURE__*/React.createElement(Spinner, {
        secondary: true,
        className: styles.loading
      })
    }), currency && /*#__PURE__*/React.createElement("span", {
      className: styles.currencyValue
    }, formatCurrency(currency.value, {
      showPlusSign: true,
      trimWholeFraction: true,
      withCurrency: false
    }))));
  };

  const renderThetanBox = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, loading && /*#__PURE__*/React.createElement(Spinner, {
      className: styles.spinner
    }), itemType && !itemType.bgStyle ? /*#__PURE__*/React.createElement("img", {
      className: styles.bgBox,
      src: itemType.bgImg
    }) : itemType && /*#__PURE__*/React.createElement("div", {
      className: classNames(styles.bgBox, styles[itemType.bgStyle])
    }), /*#__PURE__*/React.createElement("div", {
      className: styles.content
    }, /*#__PURE__*/React.createElement("div", {
      className: styles.top
    }, /*#__PURE__*/React.createElement("span", null, "OPEN BOX:\xA0", /*#__PURE__*/React.createElement("span", {
      className: styles.numberBox
    }, currentIndex, "/", amount))), /*#__PURE__*/React.createElement("div", {
      className: styles.center
    }, boxConfig && /*#__PURE__*/React.createElement("img", {
      onClick: openBox,
      src: boxConfig.imgPath,
      className: classNames(styles.thetanBox, !loading && styles.anim)
    })), /*#__PURE__*/React.createElement("div", {
      className: styles.bottom
    }, "Press any key to open")));
  };

  return /*#__PURE__*/React.createElement(Modal, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modal
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("button", {
    onClick: () => {
      // Reload Thetabox detail page after user have opened and click close modal
      if (currentIndex !== 1 || currentOpenBox) {
        onRefresh();
      }

      onClose();
    },
    className: styles.close
  }, /*#__PURE__*/React.createElement("img", {
    src: icCloseIcon
  }))), error ? renderError() : /*#__PURE__*/React.createElement("div", {
    className: styles.wrapper
  }, !currentOpenBox && renderThetanBox(), renderResult())));
};

export default OpenGiftBoxFallbackModal;